import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChatHeader } from './ChatHeader';
import { ChatContainer, ChatContainerHandle } from './ChatContainer';
import { SessionDialog } from './SessionDialog';
import { ThemedSpinner } from './ThemedSpinner';
import { ChatSidebar, SidebarPanel } from './sidebar/ChatSidebar';
import { CoachSuggestionsPanel } from './promptSuggestions/CoachSuggestionsPanel';
import { PromptSuggestionCards } from './promptSuggestions/PromptSuggestionControl';
import { GuidedToursPanel } from './sidebar/GuidedToursPanel';
import { HistoryPanel } from './sidebar/HistoryPanel';
import { SavedPromptDeleteDialog } from './preferences/SavedPromptDeleteDialog';
import { SavedPromptEditorDialog } from './preferences/SavedPromptEditorDialog';
import { SavedPromptsPanel } from './preferences/SavedPromptsPanel';
import { UserSettingsPanel } from './preferences/UserSettingsPanel';
import { useAuth } from '../hooks/useAuth';
import { useSessionHistory } from '../hooks/useSessionHistory';
import { useSavedPrompts } from '../hooks/useSavedPrompts';
import { useUserPreferences } from '../hooks/useUserPreferences';
import { useIsMobile } from '../hooks/useIsMobile';
import { useComposerDraft } from '../hooks/useComposerDraft';
import { useCoachTours } from '../tours/useCoachTours';
import { COACH_TOURS, getTourVariant } from '../tours/registry';
import type { DesktopTourController, TourSidebarState } from '../tours/types';
import { useChatContext } from '../context/ChatContext';
import { usePromptSuggestions } from '../context/PromptSuggestionsContext';
import { useTranslation } from '../localization/useAppTranslation';
import { chatService } from '../services/chatService';
import { preferencesService } from '../services/preferencesService';
import { ChatMessage } from '../types';
import type { SavedPrompt } from '../types/preferences';
import type { SavedPromptFromChatRequest } from '../types/preferences';
import type { SavedPromptRowActionState } from './preferences/SavedPromptsPanel';
import type { PromptSuggestionItem } from '../types/promptSuggestions';
import { logger } from '../utils/logger';
import { sortSavedPromptsByOrder } from '../utils/savedPrompts';
import '../styles/Chat.css';

const moduleLogger = logger.getModule('ChatLayout');
const SIDEBAR_OPEN_STORAGE_KEY = 'chat_sidebarOpen';
const SIDEBAR_PANEL_STORAGE_KEY = 'chat_sidebarPanel';
const SAVED_PROMPT_EVENT_URL_PREFIX = 'speedgauge://saved-prompt';

const isSidebarPanel = (value: string | null): value is SidebarPanel => (
  value === 'history'
  || value === 'saved-prompts'
  || value === 'coach-suggestions'
  || value === 'settings'
  || value === 'guided-tours'
);

interface PromptEditorState {
  mode: 'create' | 'edit';
  prompt: SavedPrompt | null;
  initialTitle?: string;
  initialPromptText?: string;
}

interface DeletePromptConfirmationState {
  prompt: SavedPrompt;
  error: string | null;
}

type MessageWithCachedSuggestions = {
  messageId?: number;
  metadata?: Record<string, any>;
};

export const mergeResumedMessagesWithCachedSuggestions = <
  TCached extends MessageWithCachedSuggestions,
  TResumed extends MessageWithCachedSuggestions,
>(
  cachedMessages: TCached[],
  resumedMessages: TResumed[],
): TResumed[] => {
  const cachedSuggestionsByMessageId = new Map(
    cachedMessages
      .filter((message) => (
        message.messageId !== undefined
        && Array.isArray(message.metadata?.conversationSuggestions)
      ))
      .map((message) => [message.messageId, message.metadata]),
  );

  return resumedMessages.map((message) => {
    const cachedMetadata = cachedSuggestionsByMessageId.get(message.messageId);
    if (!cachedMetadata) {
      return message;
    }

    return {
      ...message,
      metadata: {
        ...message.metadata,
        conversationSuggestions: cachedMetadata.conversationSuggestions,
        conversationSuggestionLocale: cachedMetadata.conversationSuggestionLocale,
      },
    };
  });
};

export const ChatLayout: React.FC = () => {
  const { t } = useTranslation('preferences');
  const { t: chatT } = useTranslation('chat');
  const {
    authState,
    session,
    createChatSession,
    isUserInitiatedSessionLoading,
    pendingResumeMessages,
    clearPendingResumeMessages
  } = useAuth();
  const isMobile = useIsMobile();
    const location = useLocation();
    const navigate = useNavigate();
  const {
    composerDraft,
    setComposerDraft,
    composerInputRef,
  } = useComposerDraft();
  const { messages: cachedMessages, setMessages } = useChatContext();
  const cachedMessagesRef = useRef(cachedMessages);
  cachedMessagesRef.current = cachedMessages;
  const {
    categories: suggestionCategories,
    error: suggestionsError,
    isLoading: areSuggestionsLoading,
    reload: reloadSuggestions,
    status: suggestionsStatus,
    suggestionsForSurface,
  } = usePromptSuggestions();
  const {
    historyItems,
    isLoading: historyLoading,
    hasMore,
    loadMore,
    setHistoryItems,
    fetchSessionMessages,
    deleteSession
  } = useSessionHistory();
  const {
    preferences,
    isLoading: isPreferencesLoading,
    loadError: preferencesLoadError,
    loadPreferences,
    savePreference,
  } = useUserPreferences();
  const {
    prompts: savedPrompts,
    isLoading: areSavedPromptsLoading,
    loadError: savedPromptsLoadError,
    loadSavedPrompts,
    createSavedPrompt,
    updateSavedPrompt,
    updateSavedPromptOrder,
    deleteSavedPrompt,
  } = useSavedPrompts(authState.user?.id || null, session?.sessionId || null);

  const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
    const saved = localStorage.getItem(SIDEBAR_OPEN_STORAGE_KEY)
      ?? localStorage.getItem('chat_historySidebarOpen');
    return saved === null ? true : saved === 'true';
  });
  const [activeSidebarPanel, setActiveSidebarPanel] = useState<SidebarPanel>(() => {
    const saved = localStorage.getItem(SIDEBAR_PANEL_STORAGE_KEY);
    return isSidebarPanel(saved) ? saved : 'history';
  });
  const handleSelectSidebarPanel = (panel: SidebarPanel) => {
    setActiveSidebarPanel(panel);
    setIsSidebarOpen(true);
  };
  const handleCollapseSidebarForTour = () => {
    setIsSidebarOpen(false);
  };
  const handleRestoreSidebarForTour = (state: TourSidebarState) => {
    setActiveSidebarPanel(state.activePanel);
    setIsSidebarOpen(state.isOpen);
  };
  const desktopTourController: DesktopTourController = {
    sidebarState: {
      isOpen: isSidebarOpen,
      activePanel: activeSidebarPanel,
    },
    selectSidebarPanel: handleSelectSidebarPanel,
    collapseSidebar: handleCollapseSidebarForTour,
    restoreSidebar: handleRestoreSidebarForTour,
  };
  const {
    tourProgress,
    isLoading: areToursLoading,
    loadError: toursLoadError,
    completionError: tourCompletionError,
    loadTourProgress,
    startTour,
  } = useCoachTours(
    session?.sessionId || null,
    activeSidebarPanel === 'guided-tours',
    isMobile,
    desktopTourController,
  );
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const [selectedSessionMessages, setSelectedSessionMessages] = useState<ChatMessage[]>([]);
  const [selectedSessionInfo, setSelectedSessionInfo] = useState<{
    description: string;
    startedAt: string;
  } | null>(null);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [promptEditor, setPromptEditor] = useState<PromptEditorState | null>(null);
  const promptEditorRef = useRef<PromptEditorState | null>(null);
  const isSavedPromptDraftGeneratingRef = useRef(false);
  const [deletePromptConfirmation, setDeletePromptConfirmation] = (
    useState<DeletePromptConfirmationState | null>(null)
  );
  const [promptRowActionStates, setPromptRowActionStates] = useState<
    Record<string, SavedPromptRowActionState>
  >({});
  const [isChatResponseReceiving, setIsChatResponseReceiving] = useState(false);
  const [isWelcomePanelVisible, setIsWelcomePanelVisible] = useState(true);
  const [isSettingsSaving, setIsSettingsSaving] = useState(false);
  const chatContainerRef = useRef<ChatContainerHandle>(null);
  const promptScopeKey = session
    ? JSON.stringify([session.sessionId, session.customerId])
    : null;
  const activePromptScopeKeyRef = useRef<string | null>(promptScopeKey);

  const updatePromptEditor = useCallback((nextPromptEditor: PromptEditorState | null) => {
    promptEditorRef.current = nextPromptEditor;
    setPromptEditor(nextPromptEditor);
  }, []);

  useEffect(() => {
    localStorage.setItem(SIDEBAR_OPEN_STORAGE_KEY, JSON.stringify(isSidebarOpen));
  }, [isSidebarOpen]);

  useEffect(() => {
    localStorage.setItem(SIDEBAR_PANEL_STORAGE_KEY, activeSidebarPanel);
  }, [activeSidebarPanel]);

  useEffect(() => {
    if (
      activeSidebarPanel !== 'saved-prompts'
      || !authState.isAuthenticated
    ) {
      return;
    }

    void loadSavedPrompts().catch(() => undefined);
  }, [activeSidebarPanel, authState.isAuthenticated, loadSavedPrompts]);

  useEffect(() => {
    activePromptScopeKeyRef.current = promptScopeKey;
    isSavedPromptDraftGeneratingRef.current = false;
    updatePromptEditor(null);
    setDeletePromptConfirmation(null);
    setPromptRowActionStates({});
  }, [authState.user?.id, promptScopeKey, updatePromptEditor]);

  // Add class to body to prevent scrolling when chat is active
  useEffect(() => {
    document.body.classList.add('chat-active');
    document.documentElement.classList.add('chat-active');
    
    return () => {
      document.body.classList.remove('chat-active');
      document.documentElement.classList.remove('chat-active');
    };
  }, []);

  // Update history items when session is created or changes
  useEffect(() => {
    if (session) {
      if (session.history && session.history.length > 0) {
        moduleLogger.debug('Setting history from session', { count: session.history.length });
        setHistoryItems(session.history);
      } else {
        // Clear history if session has no history (null, undefined, or empty array)
        moduleLogger.debug('Clearing history - new session has no history');
        setHistoryItems([]);
      }
    }
  }, [session, setHistoryItems]);

  useEffect(() => {
    if (pendingResumeMessages && pendingResumeMessages.length > 0) {
      const timeoutId = window.setTimeout(() => {
        setMessages(mergeResumedMessagesWithCachedSuggestions(
          cachedMessagesRef.current,
          pendingResumeMessages,
        ));
        clearPendingResumeMessages();
      }, 0);

      return () => window.clearTimeout(timeoutId);
    }
  }, [pendingResumeMessages, setMessages, clearPendingResumeMessages]);

  const handleToggleSidebar = () => {
    setIsSidebarOpen((previousValue) => !previousValue);
  };

  const handleNewSession = async () => {
    try {
      if (location.pathname !== '/') {
        navigate('/');
      }

      await createChatSession(undefined, undefined, undefined, undefined, true);
      moduleLogger.debug('New session created successfully');
    } catch (error) {
      moduleLogger.error('Failed to create new session', error);
    }
  };

  const handleSelectSession = async (sessionId: string) => {
    if (!session?.sessionId) {
      moduleLogger.error('No current session available');
      return;
    }

    if (isLoadingMessages) {
      return;
    }

    try {
      setIsLoadingMessages(true);
      setSelectedSessionId(sessionId);
      moduleLogger.debug('Loading session messages', { sessionId });

      const messages = await fetchSessionMessages(session.sessionId, sessionId);
      const historyItem = historyItems.find((item) => item.session_id === sessionId);

      if (historyItem) {
        setSelectedSessionMessages(messages);
        setSelectedSessionInfo({
          description: historyItem.description,
          startedAt: historyItem.started_at
        });
      }
    } catch (error) {
      moduleLogger.error('Failed to load session messages', { sessionId, error });
      setSelectedSessionId(null);
      setSelectedSessionMessages([]);
      setSelectedSessionInfo(null);
    } finally {
      setIsLoadingMessages(false);
    }
  };

  const handleCloseDialog = () => {
    setSelectedSessionId(null);
    setSelectedSessionMessages([]);
    setSelectedSessionInfo(null);
  };

  const handleDeleteSession = async (targetSessionId: string) => {
    if (!session?.sessionId) {
      moduleLogger.error('No current session available for delete');
      return;
    }

    try {
      moduleLogger.debug('Deleting session', { currentSessionId: session.sessionId, targetSessionId });
      await deleteSession(session.sessionId, targetSessionId);
      moduleLogger.debug('Session deleted successfully', { targetSessionId });
    } catch (error) {
      moduleLogger.error('Failed to delete session', { targetSessionId, error });
      throw error; // Re-throw to let dialog handle error display
    }
  };

  const setPromptRowActionState = (promptId: string, state: SavedPromptRowActionState) => {
    setPromptRowActionStates((currentStates) => ({
      ...currentStates,
      [promptId]: state,
    }));
  };

  const clearPromptRowActionState = (promptId: string) => {
    setPromptRowActionStates((currentStates) => {
      const nextStates = { ...currentStates };
      delete nextStates[promptId];
      return nextStates;
    });
  };

  const handleCreatePrompt = () => {
    updatePromptEditor({
      mode: 'create',
      prompt: null,
    });
  };

  const handleEditPrompt = (prompt: SavedPrompt) => {
    updatePromptEditor({
      mode: 'edit',
      prompt,
    });
  };

  const handleGenerateSavedPromptDraft = useCallback(async (
    sourcePrompt: SavedPromptFromChatRequest,
  ) => {
    if (isSavedPromptDraftGeneratingRef.current) {
      moduleLogger.debug('Ignoring duplicate saved prompt draft request');
      return;
    }

    if (promptEditorRef.current) {
      moduleLogger.debug('Ignoring saved prompt draft request because an editor is already open');
      return;
    }

    const sessionId = session?.sessionId;
    if (!sessionId) {
      throw new Error('An active chat session is required to prepare a saved prompt.');
    }
    const draftScopeKey = activePromptScopeKeyRef.current;
    if (!draftScopeKey) {
      throw new Error('An active chat session is required to prepare a saved prompt.');
    }

    isSavedPromptDraftGeneratingRef.current = true;

    try {
      const draft = await preferencesService.generateSavedPromptDraft(
        sessionId,
        sourcePrompt,
      );

      if (activePromptScopeKeyRef.current !== draftScopeKey) {
        moduleLogger.debug('Discarded saved prompt draft because the prompt scope changed');
        return;
      }

      if (promptEditorRef.current) {
        moduleLogger.warn(
          'Discarded generated saved prompt draft because an editor is already open',
          { sessionId },
        );
        return;
      }

      updatePromptEditor({
        mode: 'create',
        prompt: null,
        initialTitle: draft.title,
        initialPromptText: draft.prompt_text,
      });
    } finally {
      if (activePromptScopeKeyRef.current === draftScopeKey) {
        isSavedPromptDraftGeneratingRef.current = false;
      }
    }
  }, [session?.sessionId, updatePromptEditor]);

  const handleSavePrompt = async (title: string, promptText: string) => {
    if (!promptEditor) {
      return;
    }

    if (promptEditor.mode === 'create') {
      await createSavedPrompt({
        title,
        prompt_text: promptText,
      });
      return;
    }

    const prompt = promptEditor.prompt;
    if (!prompt) {
      return;
    }

    setPromptRowActionState(prompt.id, { isMutating: true, error: null });
    try {
      await updateSavedPrompt(prompt.id, { title, prompt_text: promptText });
      clearPromptRowActionState(prompt.id);
    } catch (error) {
      const errorMessage = error instanceof Error
        ? error.message
        : t('savedPrompts.errors.updateFailed');
      setPromptRowActionState(prompt.id, { isMutating: false, error: errorMessage });
      throw error;
    }
  };

  const handleDeletePrompt = (prompt: SavedPrompt) => {
    setDeletePromptConfirmation({ prompt, error: null });
  };

  const handleMovePrompt = async (prompt: SavedPrompt, direction: 'up' | 'down') => {
    if (!savedPrompts) {
      return;
    }

    const orderedPrompts = sortSavedPromptsByOrder(savedPrompts);
    const currentIndex = orderedPrompts.findIndex((candidate) => candidate.id === prompt.id);
    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (currentIndex < 0 || targetIndex < 0 || targetIndex >= orderedPrompts.length) {
      return;
    }

    const reorderedPrompts = [...orderedPrompts];
    [reorderedPrompts[currentIndex], reorderedPrompts[targetIndex]] = [
      reorderedPrompts[targetIndex],
      reorderedPrompts[currentIndex],
    ];

    setPromptRowActionState(prompt.id, { isMutating: true, error: null });
    try {
      await updateSavedPromptOrder(reorderedPrompts.map((candidate) => candidate.id));
      clearPromptRowActionState(prompt.id);
    } catch (error) {
      const errorMessage = error instanceof Error
        ? error.message
        : t('savedPrompts.errors.reorderFailed');
      setPromptRowActionState(prompt.id, { isMutating: false, error: errorMessage });
    }
  };

  const handleUsePrompt = (prompt: SavedPrompt) => {
    if (isChatResponseReceiving) {
      return;
    }

    const didSendPrompt = chatContainerRef.current?.sendSavedPrompt(prompt.prompt_text);
    if (!didSendPrompt) {
      return;
    }

    if (session?.sessionId) {
      void chatService.trackLinkClick(session.sessionId, {
        url: `${SAVED_PROMPT_EVENT_URL_PREFIX}/${encodeURIComponent(prompt.id)}`,
        text: prompt.title,
        is_external: false,
      });
    }

    if (isMobile) {
      setIsSidebarOpen(false);
    }
  };

  const handleUseCoachSuggestion = (suggestion: PromptSuggestionItem) => {
    if (isChatResponseReceiving) {
      return;
    }

    const didSendSuggestion = chatContainerRef.current?.sendCoachSuggestion(suggestion);
    if (didSendSuggestion && isMobile) {
      setIsSidebarOpen(false);
    }
  };

  const coachSuggestionDrawerItems = suggestionsForSurface('coach_suggestions');
  const rightPanelSuggestions = suggestionsForSurface('right_panel');
  const isRightSuggestionsPanelVisible = !isWelcomePanelVisible
    && !isSidebarOpen
    && rightPanelSuggestions.length > 0;

  const handleConfirmPromptDelete = async () => {
    const confirmation = deletePromptConfirmation;
    if (!confirmation) {
      return;
    }

    const { prompt } = confirmation;
    setPromptRowActionState(prompt.id, { isMutating: true, error: null });
    setDeletePromptConfirmation((currentConfirmation) => (
      currentConfirmation ? { ...currentConfirmation, error: null } : null
    ));
    try {
      await deleteSavedPrompt(prompt.id);
      clearPromptRowActionState(prompt.id);
      setDeletePromptConfirmation(null);
    } catch (error) {
      const errorMessage = error instanceof Error
        ? error.message
        : t('savedPrompts.errors.deleteFailed');
      setPromptRowActionState(prompt.id, { isMutating: false, error: errorMessage });
      setDeletePromptConfirmation((currentConfirmation) => (
        currentConfirmation ? { ...currentConfirmation, error: errorMessage } : null
      ));
    }
  };

  return (
    <div className="chat-layout">
      <ChatHeader
        showSidebarToggle={true}
        isSidebarOpen={isSidebarOpen}
        onToggleSidebar={handleToggleSidebar}
        onNewSession={handleNewSession}
      />
      <div className="chat-body">
        <ChatSidebar
          activePanel={activeSidebarPanel}
          isOpen={isSidebarOpen}
          isNewSessionLoading={isUserInitiatedSessionLoading}
          onCollapse={handleToggleSidebar}
          onSelectPanel={handleSelectSidebarPanel}
          onNewSession={handleNewSession}
          titleAdornment={activeSidebarPanel === 'settings' && (
            isPreferencesLoading || isSettingsSaving
          ) ? (
            <ThemedSpinner
              size="sm"
              ariaLabel={isPreferencesLoading ? t('settings.loading') : t('settings.saving')}
            />
          ) : null}
        >
          {activeSidebarPanel === 'history' && (
            <HistoryPanel
              historyItems={historyItems}
              onSelectSession={handleSelectSession}
              onLoadMore={loadMore}
              hasMore={hasMore}
              isLoading={historyLoading}
              isLoadingSession={isLoadingMessages}
              loadingSessionId={isLoadingMessages ? selectedSessionId : null}
              currentSessionId={session?.sessionId || ''}
            />
          )}
          {activeSidebarPanel === 'settings' && (
            <UserSettingsPanel
              preferences={preferences}
              isLoading={isPreferencesLoading}
              loadError={preferencesLoadError}
              onRetryLoad={loadPreferences}
              onSavePreference={savePreference}
              onSavingStateChange={setIsSettingsSaving}
            />
          )}
          {activeSidebarPanel === 'saved-prompts' && (
            <SavedPromptsPanel
              prompts={savedPrompts}
              isLoading={areSavedPromptsLoading}
              loadError={savedPromptsLoadError}
              onRetryLoad={loadSavedPrompts}
              rowActionStates={promptRowActionStates}
              onCreatePrompt={handleCreatePrompt}
              onEditPrompt={handleEditPrompt}
              onDeletePrompt={handleDeletePrompt}
              onMovePrompt={handleMovePrompt}
              canSendPrompt={!isChatResponseReceiving}
              onUsePrompt={handleUsePrompt}
            />
          )}
          {activeSidebarPanel === 'coach-suggestions' && (
            <CoachSuggestionsPanel
              categories={suggestionCategories}
              suggestions={coachSuggestionDrawerItems}
              isLoading={areSuggestionsLoading}
              status={suggestionsStatus}
              error={suggestionsError}
              canSendSuggestion={!isChatResponseReceiving}
              onRetryLoad={reloadSuggestions}
              onUseSuggestion={handleUseCoachSuggestion}
            />
          )}
          {activeSidebarPanel === 'guided-tours' && (
            <GuidedToursPanel
              tours={COACH_TOURS}
              variant={getTourVariant(isMobile)}
              progress={tourProgress}
              isLoading={areToursLoading}
              loadError={toursLoadError}
              completionError={tourCompletionError}
              onRetryLoad={loadTourProgress}
              onStartTour={startTour}
            />
          )}
        </ChatSidebar>
        <div className={`chat-content ${isRightSuggestionsPanelVisible ? 'chat-content-with-right-panel' : ''}`}>
          <div className={`chat-main ${isUserInitiatedSessionLoading ? 'loading-session' : ''}`}>
            <ChatContainer
              ref={chatContainerRef}
              composerDraft={composerDraft}
              onComposerDraftChange={setComposerDraft}
              composerInputRef={composerInputRef}
              onResponseStateChange={setIsChatResponseReceiving}
              onWelcomeStateChange={setIsWelcomePanelVisible}
              onGenerateSavedPromptDraft={session?.sessionId
                ? handleGenerateSavedPromptDraft
                : undefined}
            />
          </div>
          {isRightSuggestionsPanelVisible && (
            <aside className="coach-suggestions-right-panel" aria-label={chatT('suggestions.rightPanel')}>
              <h2>{chatT('suggestions.rightPanel')}</h2>
              <PromptSuggestionCards
                suggestions={rightPanelSuggestions}
                maxSuggestions={6}
                disabled={isChatResponseReceiving}
                onSelect={handleUseCoachSuggestion}
              />
            </aside>
          )}
        </div>
      </div>
      {selectedSessionId && selectedSessionInfo && (
        <SessionDialog
          isOpen={true}
          sessionId={selectedSessionId}
          currentSessionId={session?.sessionId || ''}
          description={selectedSessionInfo.description}
          startedAt={selectedSessionInfo.startedAt}
          messages={selectedSessionMessages}
          onClose={handleCloseDialog}
          onDelete={handleDeleteSession}
          onGenerateSavedPromptDraft={session?.sessionId
            ? handleGenerateSavedPromptDraft
            : undefined}
        />
      )}
      {promptEditor && (
        <SavedPromptEditorDialog
          isOpen={true}
          mode={promptEditor.mode}
          prompt={promptEditor.prompt}
          initialTitle={promptEditor.initialTitle}
          initialPromptText={promptEditor.initialPromptText}
          onSave={handleSavePrompt}
          onClose={() => updatePromptEditor(null)}
        />
      )}
      {deletePromptConfirmation && (
        <SavedPromptDeleteDialog
          isOpen={true}
          prompt={deletePromptConfirmation.prompt}
          isDeleting={Boolean(promptRowActionStates[deletePromptConfirmation.prompt.id]?.isMutating)}
          error={deletePromptConfirmation.error}
          onConfirm={handleConfirmPromptDelete}
          onClose={() => setDeletePromptConfirmation(null)}
        />
      )}
    </div>
  );
};
